import {
	Component,
	Directive,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Renderer2,
	SimpleChanges,
	ViewContainerRef
} from '@angular/core';
import {DICTIONARY} from '../dictionary';

@Component({
	selector: 'video-subtitle-controls',
	template: `
		<div (clickOutside)="showMenu = false;"
			 *ngIf="completedSubtitles?.length"
			 class="static-color"
			 style="position: absolute; top: 10px; right: 14px; z-index: 10;">
			<button (click)="showMenu = !showMenu"
					style="border: 1px solid rgba(255,255,255,0.4); border-radius: 4px; padding: 3px 8px; font-size: 12px; font-weight: 700; cursor: pointer; letter-spacing: 0.5px;"
					[style.background]="selectedLang ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)'"
					[style.color]="selectedLang ? '#000' : '#fff'">
				CC
			</button>
			<div *ngIf="showMenu"
				 style="position: absolute; right: 0; top: 32px; background: rgba(20,20,20,0.95); border-radius: 6px; padding: 6px 0; min-width: 140px; box-shadow: 0 4px 16px rgba(0,0,0,0.5);">
				<div (click)="selectLang(null)"
					 style="padding: 7px 16px; cursor: pointer; color: white; font-size: 13px;"
					 [style.font-weight]="!selectedLang ? '700' : '400'">
					Off
				</div>
				<div *ngFor="let sub of completedSubtitles"
					 (click)="selectLang(sub.lang)"
					 style="padding: 7px 16px; cursor: pointer; color: white; font-size: 13px;"
					 [style.font-weight]="selectedLang === sub.lang ? '700' : '400'">
					{{dic.CONSTANTS.subtitleLanguages[sub.lang] || sub.lang}}
				</div>
				<div *ngIf="selectedLang" style="border-top: 1px solid rgba(255,255,255,0.15); margin: 4px 0;"></div>
				<div *ngIf="selectedLang" style="display: flex; justify-content: space-around; padding: 5px 16px 7px;">
					<span (click)="setFontSize(0.75)" style="cursor: pointer; color: white; font-size: 11px; padding: 2px 6px; border-radius: 3px;"
						  [style.background]="fontSize === 0.75 ? 'rgba(255,255,255,0.25)' : 'transparent'">S</span>
					<span (click)="setFontSize(1)" style="cursor: pointer; color: white; font-size: 13px; padding: 2px 6px; border-radius: 3px;"
						  [style.background]="fontSize === 1 ? 'rgba(255,255,255,0.25)' : 'transparent'">M</span>
					<span (click)="setFontSize(1.5)" style="cursor: pointer; color: white; font-size: 16px; padding: 2px 6px; border-radius: 3px;"
						  [style.background]="fontSize === 1.5 ? 'rgba(255,255,255,0.25)' : 'transparent'">L</span>
				</div>
			</div>
		</div>
	`,
})
export class VideoSubtitleControlsComponent {
	completedSubtitles: any[] = [];
	showMenu = false;
	selectedLang = null;
	fontSize = 1;
	dic = DICTIONARY;

	onLangChange: (lang: string | null) => void;
	onFontSizeChange: (size: number) => void;

	selectLang(lang) {
		this.selectedLang = lang;
		this.showMenu = false;
		this.onLangChange?.(lang);
	}

	setFontSize(size) {
		this.fontSize = size;
		this.onFontSizeChange?.(size);
	}

	reset() {
		this.showMenu = false;
		this.selectedLang = null;
		this.fontSize = 1;
	}
}

@Directive({
	selector: 'video[videoSubtitles]',
})
export class SubtitlesDirective implements OnInit, OnChanges, OnDestroy {
	@Input() videoSubtitles: any[];

	videoEl: HTMLVideoElement;
	styleEl: HTMLStyleElement;
	controlsRef;
	activeLang: string | null = null;
	currentVttUrl = null;

	onFullscreenChange = () => {
		if (this.activeLang) {
			this.applyFontSize(this.controlsRef.instance.fontSize);
		}
	};

	constructor(
		private el: ElementRef<HTMLVideoElement>,
		private renderer: Renderer2,
		private viewContainerRef: ViewContainerRef
	) {
		this.videoEl = this.el.nativeElement;
	}

	ngOnInit() {
		if (!this.videoEl.id) {
			this.videoEl.id = 'subtitles-video-' + Math.random().toString(36).slice(2);
		}

		this.styleEl = document.createElement('style');
		document.head.appendChild(this.styleEl);

		this.videoEl.addEventListener('loadedmetadata', () => {
			this.applySubtitleTrack(this.activeLang);
		});

		this.controlsRef = this.viewContainerRef.createComponent(VideoSubtitleControlsComponent);
		this.controlsRef.instance.onLangChange = (lang) => {
			this.activeLang = lang;
			this.applySubtitleTrack(lang);
			if (lang) {
				this.applyFontSize(this.controlsRef.instance.fontSize);
			}
		};
		this.controlsRef.instance.onFontSizeChange = (size) => {
			this.applyFontSize(size);
		};

		this.videoEl.addEventListener('fullscreenchange', this.onFullscreenChange);
		this.videoEl.addEventListener('webkitfullscreenchange', this.onFullscreenChange);

		this.controlsRef.instance.completedSubtitles = (this.videoSubtitles || []).filter(sub => sub.segments);
		this.controlsRef.changeDetectorRef.detectChanges();

		const parent = this.videoEl.parentElement;
		parent.style.position = 'relative';
		this.renderer.appendChild(parent, this.controlsRef.location.nativeElement);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['videoSubtitles']) {
			const completed = (this.videoSubtitles || []).filter(sub => sub.segments);
			if (this.controlsRef) {
				this.activeLang = null;
				this.controlsRef.instance.completedSubtitles = completed;
				this.controlsRef.instance.reset();
				this.controlsRef.changeDetectorRef.detectChanges();
				this.applySubtitleTrack(null);
			}
		}
	}

	ngOnDestroy() {
		this.controlsRef?.destroy();
		this.styleEl?.parentNode?.removeChild(this.styleEl);
		this.videoEl.removeEventListener('fullscreenchange', this.onFullscreenChange);
		this.videoEl.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
		if (this.currentVttUrl) {
			URL.revokeObjectURL(this.currentVttUrl);
		}
	}

	applySubtitleTrack(langCode) {
		Array.from(this.videoEl.querySelectorAll('track')).forEach(track => track.remove());
		for (let i = this.videoEl.textTracks.length - 1; i >= 0; i--) {
			this.videoEl.textTracks[i].mode = 'disabled';
		}

		if (!langCode) {
			return;
		}

		const entry = (this.videoSubtitles || []).find(sub => sub.lang === langCode);
		if (!entry?.segments) {
			return;
		}

		const segments = JSON.parse(entry.segments);
		const vttContent = buildVtt(segments);
		if (this.currentVttUrl) {
			URL.revokeObjectURL(this.currentVttUrl);
			this.currentVttUrl = null;
		}
		const vttBlob = new Blob([vttContent], {type: 'text/vtt'});
		const vttUrl = URL.createObjectURL(vttBlob);
		this.currentVttUrl = vttUrl;

		const trackEl = document.createElement('track');
		trackEl.kind = 'subtitles';
		trackEl.srclang = entry.lang;
		trackEl.src = vttUrl;
		trackEl.default = true;
		this.videoEl.appendChild(trackEl);

		trackEl.addEventListener('load', () => {
			if (this.videoEl.textTracks[0]) {
				this.videoEl.textTracks[0].mode = 'showing';
			}
		});
	}

	applyFontSize(size) {
		// px based on video height — avoids Safari ::cue em-cascading bug and scales correctly in fullscreen
		const videoHeight = this.videoEl.clientHeight || 360;
		const basePx = Math.round(videoHeight * 0.04);
		this.styleEl.textContent = `#${this.videoEl.id}::cue { font-size: ${Math.round(basePx * size)}px; }`;
	}
}

function buildVtt(segments: any[]): string {
	const lines: string[] = ['WEBVTT', ''];
	segments.forEach((seg: any, idx: number) => {
		lines.push(String(idx + 1));
		lines.push(`${formatVttTime(seg.start)} --> ${formatVttTime(seg.end)}`);
		lines.push(seg.text.trim());
		lines.push('');
	});
	return lines.join('\n');
}

function formatVttTime(totalSeconds: number): string {
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const secs = totalSeconds % 60;
	const secsInt = Math.floor(secs);
	const millis = Math.round((secs - secsInt) * 1000);
	return `${pad(hours)}:${pad(minutes)}:${pad(secsInt)}.${String(millis).padStart(3, '0')}`;
}

function pad(num: number): string {
	return String(num).padStart(2, '0');
}
